import { isSponsoredProduct } from '@shared/types/types';
import type { AppsTracking } from '@tilelist/application/ports/AppsTracking.ts';
import { type TileListItem, isProductItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';

type EventName = { ios: string; android: string };

const eventNames = {
    Produktliste: {
        android: 'tap3xe',
        ios: 'duht9i',
    },
    Suchergebnisseite: {
        android: '9ldrbv',
        ios: 'kxrhs8',
    },
} satisfies Record<string, EventName>;

type PageTypes = keyof typeof eventNames;

const isPageType = (pageType: string | undefined): pageType is PageTypes => !!pageType && pageType in eventNames;

const MAX_ITEMS_TO_TRACK = 4;

let trackAdjustPromise: Promise<typeof import('@otto-ec/apps-api/thirdpartytracking')> | null = null;

const getTrackAdjust = () => {
    trackAdjustPromise ??= import('@otto-ec/apps-api/thirdpartytracking');
    return trackAdjustPromise;
};

export function createAppsTracking(): AppsTracking {
    return {
        async track(request) {
            const { trackAdjust } = await getTrackAdjust();
            void trackAdjust({
                eventName: request.eventName,
                partnerParameters: {
                    articleids: JSON.stringify(request.articleNumbers),
                    contentType: 'product',
                    ...(request.sitePath ? { sitePath: request.sitePath } : {}),
                },
            });
        },
    };
}

export async function trackAdjustForTileList(
    tileListItems: TileListItem[],
    pageType: string | undefined,
    sitePath: string,
    onex: OnEx | undefined,
    tracking: AppsTracking,
): Promise<void> {
    if (onex?.isToggleActive('EMERGENCY_DISABLE_APPS_TRACKING')) return;

    if (!pageType) {
        const eventName = eventNames['Produktliste']; /*      */
        await tracking.track({
            eventName,
            articleNumbers: [],
        });

        return;
    }

    if (!isPageType(pageType)) return;

    const eventName = eventNames[pageType];

    const articleNumbersToTrack = tileListItems
        .filter(isProductItem)
        .filter(({ product }) => !isSponsoredProduct(product))
        .map(({ product }) => product.articleNumber)
        .filter((articleNumber): articleNumber is string => Boolean(articleNumber))
        .slice(0, MAX_ITEMS_TO_TRACK);

    await tracking.track({
        eventName,
        articleNumbers: articleNumbersToTrack,
        sitePath,
    });
}
